/**
 * @file resolveAttachmentCapacityCaption.ts
 * @description Builds the panel's capacity caption — how large one file may be
 * and how full the document is. Consumer: `AttachmentsUI`, which renders it at
 * the foot of the panel.
 *
 * Both figures are read off the constants the server enforces,
 * `MAX_ATTACHMENT_FILE_BYTES` and `MAX_ATTACHMENTS_PER_DOCUMENT`, never written
 * as literals: the caption is only worth showing while the number on screen is
 * the number that refuses an upload. `formatBytes` is 1024-based with `GB` as
 * its label, so the 5 GiB cap prints as `5GB`.
 *
 * It reads "Under", not "Up to". The cap is exclusive everywhere it is enforced
 * — `beginAttachmentVersion` refuses `>=` and `storage.rules` admits only `<` —
 * so a file of exactly 5 GiB is refused, and "Up to" would promise the one size
 * `resolveAttachmentSizeRefusal` turns away.
 *
 * The count is attachments, not versions — a file's history costs a document
 * none of its 300 slots.
 */
import { MAX_ATTACHMENT_FILE_BYTES, MAX_ATTACHMENTS_PER_DOCUMENT } from '#types';
import { formatBytes } from './formatBytes';

/**
 * @function resolveAttachmentCapacityCaption
 * @description States the per-file size cap and the document's attachment usage.
 * @param {number} attachmentCount - How many attachments the artifact holds now.
 * @returns {string} The caption to render.
 */
export const resolveAttachmentCapacityCaption = (attachmentCount: number): string =>
    `Under ${formatBytes(MAX_ATTACHMENT_FILE_BYTES)} per file`
    + ` · ${attachmentCount} of ${MAX_ATTACHMENTS_PER_DOCUMENT} files used`;
